import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Col, Row, Card } from 'react-bootstrap';
import ApiService from '../../services/ApiService';
import { CardTransaction, CashTransaction, PaymentSession } from './types';
import ContentContainer from '../ContentContainer';
import CurrencyItem from '../form/misc/CurrencyItem';
import { getCardImageByName } from './PaymentUtil';
import { extractTimestamp } from '../../util/general';
import FileNotFound from '../FileNotFound';
import LoadingPage from '../LoadingPage';

interface Props {
  id?: string;
  cardTransactionId?: string;
}

const CashTransactionCard = ({ tx }: { tx: CashTransaction }) => {
  const variant = tx.amount === 0 ? 'danger' : 'success';
  const date = new Date(extractTimestamp(tx.id));

  return (
    <Card className="mb-2" border={variant}>
      <Card.Header className={`text-uppercase bg-${variant} text-white`} style={{ fontSize: '17px' }}>
        <span className="float-right" style={{ fontSize: '12px', lineHeight: '30px' }}>
          {date.toLocaleString()}
        </span>
        <i className="fad fa-money-bill" /> &nbsp; Cash
      </Card.Header>
      <Card.Body style={{ background: 'white', fontSize: '14px' }}>
        <Row className="mb-2">
          <Col md={4}>
            <strong>Amount</strong>
          </Col>
          <Col md={8}>
            <CurrencyItem>{tx.amount}</CurrencyItem>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col md={4}>
            <strong>User Initials</strong>
          </Col>
          <Col md={8}>{tx.userInitials}</Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const CardTransactionCard = ({ tx }: { tx: CardTransaction }) => {
  const variant = ['refund', 'void'].includes(tx.transactionType) ? 'danger' : 'success';
  const date = new Date(extractTimestamp(tx.id));

  const renderTransaction = () => {
    if (['refund', 'void'].includes(tx.transactionType)) {
      return (
        <>
          -<CurrencyItem>{tx.amount}</CurrencyItem>
        </>
      );
    }

    // usually just sale
    return <CurrencyItem>{tx.amount}</CurrencyItem>;
  };

  return (
    <Card className="mb-2" border={variant}>
      <Card.Header className={`text-uppercase bg-${variant} text-white`} style={{ fontSize: '17px' }}>
        <span className="float-right" style={{ fontSize: '12px', lineHeight: '30px' }}>
          {date.toLocaleString()}
        </span>
        <i className="fad fa-credit-card" /> &nbsp; {tx.transactionType}
      </Card.Header>
      <Card.Body style={{ background: 'white', fontSize: '14px' }}>
        <Row className="mb-2">
          <Col md={4}>
            <strong>Amount</strong>
          </Col>
          <Col md={8}>{renderTransaction()}</Col>
        </Row>

        {tx.terminalName && (
          <Row className="mb-2">
            <Col md={4}>
              <strong>Terminal</strong>
            </Col>
            <Col md={8}>{tx.terminalName}</Col>
          </Row>
        )}

        <Row className="mb-2">
          <Col md={4}>
            <strong>Card</strong>
          </Col>
          <Col md={8}>
            <img src={getCardImageByName(tx.cardType)} style={{ width: '40px' }} /> ending in {tx.cardLast4}
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={4}>
            <strong>User Initials</strong>
          </Col>
          <Col md={8}>{tx.userInitials}</Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const PaymentSessionDetail = ({ id, cardTransactionId }: Props) => {
  const [paymentSession, setPaymentSession] = useState<PaymentSession>();
  const [error, setError] = useState('');

  const loadData = async () => {
    try {
      if (id) {
        const paymentResponse = await ApiService.get(`/db/UPY4/${id}`);
        setPaymentSession(paymentResponse.data);
      } else if (cardTransactionId) {
        const txResponse = await ApiService.get(`/db/UPY3/${cardTransactionId}`);
        if (!txResponse.data?.sessionId) {
          setError('Payment Session not found');
          return;
        }

        const paymentResponse = await ApiService.get(`/db/UPY4/${txResponse.data.sessionId}`);
        setPaymentSession(paymentResponse.data);
      }
    } catch (err: any) {
      console.log(err);
      setError(err.message);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (error) {
    return <FileNotFound message={error} />;
  }

  if (!paymentSession) {
    return <LoadingPage />;
  }

  // in this page we only care about the cash result
  const latestCashTransaction = _.first(_.orderBy(paymentSession.CashTransactions, ['id'], ['desc']));

  // first transaction on top
  paymentSession.CardTransactions = _.orderBy(paymentSession.CardTransactions, ['id'], ['asc']);

  return (
    <ContentContainer>
      <Row>
        <Col md={12}>
          <h5>
            {paymentSession.customerName} ({paymentSession.customerNum})
          </h5>
        </Col>
        <Col md={6}>
          {paymentSession.CardTransactions.map((tx: CardTransaction, i: number) => (
            <CardTransactionCard tx={tx} key={i} />
          ))}
          {latestCashTransaction && <CashTransactionCard tx={latestCashTransaction} />}
        </Col>
      </Row>
    </ContentContainer>
  );
};

export default PaymentSessionDetail;
